import React from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Col, Row } from 'reactstrap';

import Card from 'components/App/Card';
import AppLoading from 'components/App/Loading';
import CustomActiveShapePieChart from 'components/Charts/PieChart';
import Selection from 'components/App/Selection';

import { ANALYTIC_TYPE } from 'constants/analytics';
import { ROOM_TYPE } from 'constants/index';
import INITIAL_VALUE from 'constants/initialValue';
import { getParamsFormObject } from 'utils';

import styles from './RevenuaDistribution.module.scss';

const OPTION_FILTER_BY_TIME = [
  {
    label: '365 ngày qua',
    value: 365,
  },
  {
    label: '90 ngày qua',
    value: 90,
  },
  {
    label: '30 ngày qua',
    value: 30,
  },
  {
    label: '14 ngày qua',
    value: 14,
  },
  {
    label: '7 ngày qua',
    value: 7,
  },
];

function RevenuaDistribution(props) {
  const {
    isLoading,
    overview,
    chart,
  } = props;

  const history = useHistory();
  const { search } = useLocation();
  const searchQuery = React.useMemo(() => {
    const params = new URLSearchParams(search);

    return {
      place: params.get('place'),
      days: params.get('days') || 365,
      type: params.get('type') || ANALYTIC_TYPE.REVENUE_DISTRIBUTION,
    };
  }, [search]);

  const onChangeFilterByTime = React.useCallback((e) => {
    history.push({
      search: getParamsFormObject({
        ...searchQuery,
        days: e.value,
      }),
    });
  }, [searchQuery, history]);

  const percentRevenue = React.useCallback((value, arr) => `${((value / arr.reduce((accumulator, currentObject) => accumulator + currentObject.revenue, 0)) * 100).toFixed(1)}%`, []);

  return (
    <div className={styles.Components}>
      <AppLoading loading={isLoading} loaderOnPage />
      <Card className={styles.Section}>
        <div className={styles.Filter}>
          <div className={styles.FilterItem}>
            <span className={styles.Label}>
              Cho xem dữ liệu trong:
            </span>

            <Selection
              onChange={onChangeFilterByTime}
              options={OPTION_FILTER_BY_TIME}
              selectedClassName={styles.Selection}
              value={+searchQuery.days}
            />
          </div>
        </div>

        <div className={styles.Result}>
          <div className={`${styles.ResultItem} ${styles.PresetTime}`}>
            <p>Loại phòng được đặt nhiều nhất</p>
            <p>
              {ROOM_TYPE[overview.maxBookingRoom?.roomName]?.label
                || overview.maxBookingRoom?.name
                || 'No data'}
            </p>
          </div>
          <div className={styles.ResultItem}>
            <p>Phương án giá được đặt nhiều nhất</p>
            <p>{overview.maxBookingPriceType?.priceTypeName}</p>
          </div>
        </div>
      </Card>

      <Row>
        {chart.roomStatistics && (
          <Col md="6">
            <Card className={styles.Section}>
              <p className={styles.Title}>Phân phối doanh thu theo Loại phòng</p>

              <CustomActiveShapePieChart
                data={chart.roomStatistics.map((item) => ({
                  name: ROOM_TYPE[item.roomName]?.label || item.name || 'No data',
                  value: item.revenue,
                }))}
              />

              <table className={styles.Table}>
                <thead className={styles.TableHeading}>
                  <tr className={styles.TableHeadingRow}>
                    <th>Loại phòng</th>
                    <th>Tỉ lệ doanh thu thuần</th>
                  </tr>
                </thead>

                <tbody className={styles.TableBody}>
                  {chart.roomStatistics.map((item, index) => (
                    <tr
                      key={item.roomName || item.room || item.name || index}
                      className={styles.TableBodyRow}
                    >
                      <td>
                        {ROOM_TYPE[item.roomName]?.label || item.name || 'No data'}
                      </td>
                      <td>
                        {percentRevenue(item.revenue, chart.roomStatistics)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </Col>
        )}

        {chart.priceTypesStatistics && (
        <Col md="6">
          <Card className={styles.Section}>
            <p className={styles.Title}>Phân phối doanh thu theo Phương án giá</p>

            <CustomActiveShapePieChart
              data={chart.priceTypesStatistics.map((item) => ({
                name: item.priceTypeName,
                value: item.revenue,
              }))}
            />

            <table className={styles.Table}>
              <thead className={styles.TableHeading}>
                <tr className={styles.TableHeadingRow}>
                  <th>Phương án giá</th>
                  <th>Tỉ lệ doanh thu thuần</th>
                </tr>
              </thead>

              <tbody className={styles.TableBody}>
                {chart.priceTypesStatistics.map((item) => (
                  <tr key={item.priceType} className={styles.TableBodyRow}>
                    <td>{item.priceTypeName}</td>
                    <td>{percentRevenue(item.revenue, chart.priceTypesStatistics)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </Col>
        )}
      </Row>
    </div>
  );
}

RevenuaDistribution.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  overview: PropTypes.instanceOf(Object),
  chart: PropTypes.instanceOf(Object),
};

RevenuaDistribution.defaultProps = {
  overview: INITIAL_VALUE.OBJECT,
  chart: INITIAL_VALUE.OBJECT,
};

export default React.memo(RevenuaDistribution);
